import { action, makeObservable, observable, computed } from 'mobx';
import * as ContractTypes from '@draftkings/dk-data-layer';
import {
    getDefaultValue,
    hasSgpToggle,
    encodeLink,
    getFullDate,
    getLocalizedUrl,
    isEventLive
} from '@draftkings/sportsbook-common-utils';
import { TrackEventFunction } from '@draftkings/sportsbook-common-contracts';
import { IParser, ParserOptions, EventCard, SliderCard } from '../contracts';
import { SherpaData, Retriever } from '../contracts/retriever';
import { parseLiveGameState, getTeamsInfo } from '../utils';
import { EVENT_SLIDER_TRACKING } from '../constants';

type ParserMobx = 'cards' | 'toggleCardId' | 'excludeId';
type Params = {
    events: ContractTypes.SportEvent[];
    leagues: ContractTypes.League[];
};
type CardTypeHandlerIParams = {
    event?: ContractTypes.SportEvent;
    league?: ContractTypes.League;
    widgetId?: string;
};

const outrightParticipants: Record<string, string> = {
    multiteam: 'multiteam',
    multiplayer: 'multiplayer'
};

export class Parser implements IParser {
    private retriever: Retriever<SherpaData>;
    private logos: ParserOptions['logos'];
    private productConfig: ParserOptions['productConfig'];
    private excludeId: string;
    private staticS3Host: string;
    private swooshBaseUrl: string;
    private isSwooshTeamLogosEnabledForSportSlider: boolean;
    private locale: string;
    private useLocale: boolean;
    private trackEvent: TrackEventFunction;

    constructor(options: ParserOptions) {
        this.retriever = options.retriever;
        this.logos = options.logos;
        this.productConfig = options.productConfig;
        this.excludeId = options.excludeId;
        this.staticS3Host = options.staticS3Host;
        this.swooshBaseUrl = options.swooshBaseUrl;
        this.isSwooshTeamLogosEnabledForSportSlider = options.isSwooshTeamLogosEnabledForSportSlider;
        this.locale = options.locale;
        this.useLocale = options.useLocale;
        this.trackEvent = options.trackEvent;

        makeObservable<IParser, ParserMobx>(this, {
            cards: computed,
            excludeId: observable,
            toggleCardId: action
        });
    }

    private createEventCards: (params: Params) => EventCard[] = ({ events, leagues }) => {
        return events
            .filter((event) => event.id !== this.excludeId)
            .sort((e1, e2) => {
                const date1 = new Date(e1.startEventDate);
                const date2 = new Date(e2.startEventDate);
                if (date1 > date2) {
                    return 1;
                }
                if (date2 > date1) {
                    return -1;
                }
                return 0;
            })
            .map((event) =>
                this.createEventCard({
                    event,
                    league: leagues[0]
                })
            )
            .filter((card): card is EventCard => card !== undefined);
    };

    private getEventUrl = (name: string, id: string) => {
        const eventUrl = `/event/${encodeLink(name)}/${id}`;

        return getLocalizedUrl(eventUrl, this.locale, this.useLocale);
    };

    private createEventCard = ({ event, league, widgetId }: CardTypeHandlerIParams) => {
        if (
            event &&
            !outrightParticipants[getDefaultValue(event.eventParticipantType?.replace(/\s+/g, '')?.toLowerCase(), '')]
        ) {
            const eventStatus = parseLiveGameState(event.liveGameState, event.status);
            const id = getDefaultValue(widgetId, event.id);
            const { startEventDate } = event;
            const { teams, scores } = getTeamsInfo({
                mainScorecard: event.eventScorecard?.mainScorecard,
                participants: getDefaultValue(event.participants, []),
                logosMap: this.logos[getDefaultValue(event.leagueId, getDefaultValue(league?.id, ''))],
                staticS3Host: this.staticS3Host,
                swooshBaseUrl: this.swooshBaseUrl,
                isSwooshTeamLogosEnabledForSportSlider: this.isSwooshTeamLogosEnabledForSportSlider,
                sportId: league?.sportId,
                isTeamSwap: league?.isTeamSwap,
                eventStatus: event.status
            });
            const eventCard: EventCard = {
                id,
                isLive: event.status === 'STARTED',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                isSgp: hasSgpToggle(this.productConfig as any, getDefaultValue(event.tags, [])),
                teams,
                scores,
                name: event.name,
                startDate: getFullDate(startEventDate, this.locale),
                period: eventStatus.period,
                timer: eventStatus.timer,
                href: this.getEventUrl(event.name, event.id)
            };
            return eventCard;
        }

        return undefined;
    };

    private get haveAllEventsFinished(): boolean {
        return [...this.retriever.data.events.values()].every((event) => event.status === 'FINISHED');
    }

    get cards(): SliderCard[] {
        return this.createEventCards({
            events: Array.from(this.retriever.data.events.values()),
            leagues: Array.from(this.retriever.data.leagues.values())
        });
    }

    toggleCardId(cardId: string): void {
        this.excludeId = cardId;

        const event = this.retriever.data.events.get(cardId);
        if (event) {
            const { sports, leagues } = this.retriever.data as SherpaData;
            const { name: leagueName = '', id: leagueId = '' } = [...leagues.values()][0];
            const { name: sportName = '', id: sportId = '' } = [...sports.values()][0];

            this.trackEvent(EVENT_SLIDER_TRACKING.CONTEXT, {
                source: EVENT_SLIDER_TRACKING.SOURCE,
                context: EVENT_SLIDER_TRACKING.CONTEXT,
                eventId: event.id,
                league: leagueName,
                leagueId,
                sport: sportName,
                sportId,
                eventState: isEventLive(event.status) ? EVENT_SLIDER_TRACKING.LIVE : EVENT_SLIDER_TRACKING.PRE_LIVE
            });

            this.haveAllEventsFinished && this.retriever.removeAllEvents?.();
        }
    }

    activate(): void {}
    deactivate(): void {}
}
